/**
 * Preferred vendor vs. list-price vendor — two independent concepts.
 *
 * Preferred vendor (inventory_item.vendor_id) is whom the company buys the
 * part from; purchasing groups review items by it. List-price vendor is whose
 * price fills the form's list price. They may differ, so neither selection is
 * allowed to write the other's fields.
 *
 * The preferred-vendor dropdown offers the company's whole vendor list, not
 * just vendors linked to the item — a part can be bought from a vendor that
 * has no vendor_item_details row yet.
 */

export type PreferredVendorOption = {
  label: string;
  value: string | number | null;
};

type CompanyVendorRow = {
  vendor_id: string | number;
  vendor_name: string;
};

type LinkedVendorRow = {
  vendor_id: string | number;
  vendor_name: string;
  vendor_part_number: string | null;
  vendor_item_price: number | null;
};

export const buildPreferredVendorOptions = (
  companyVendors: CompanyVendorRow[] | undefined,
  noneLabel: string
): PreferredVendorOption[] => [
  { label: noneLabel, value: null },
  ...(companyVendors ?? [])
    .slice()
    .sort((a, b) => (a.vendor_name || "").localeCompare(b.vendor_name || ""))
    .map((vendor) => ({
      label: vendor.vendor_name,
      value: vendor.vendor_id,
    })),
];

export const findPreferredVendorOption = (
  options: PreferredVendorOption[],
  vendorId: string | number | null | undefined
): PreferredVendorOption | null => {
  if (vendorId == null || vendorId === "") return null;
  return (
    options.find(
      (option) =>
        option.value != null && String(option.value) === String(vendorId)
    ) ?? null
  );
};

/**
 * Patch for a preferred-vendor selection. Only vendor_id / vendor_name change —
 * list-price fields are untouched.
 */
export const applyPreferredVendorChange = <
  T extends { vendor_id: unknown; vendor_name: unknown }
>(
  values: T,
  option: PreferredVendorOption | null,
  companyVendors: CompanyVendorRow[] | undefined
): T => {
  if (option?.value == null) {
    return { ...values, vendor_id: null, vendor_name: "" };
  }
  const match = (companyVendors ?? []).find(
    (v) => String(v.vendor_id) === String(option.value)
  );
  return {
    ...values,
    vendor_id: option.value,
    vendor_name: match?.vendor_name ?? option.label ?? "",
  };
};

/**
 * Patch for a list-price-vendor selection. Copies that vendor's part number and
 * price into the price fields (or restores the original list price on clear)
 * and never touches vendor_id / vendor_name — choosing whose price to display
 * must not change whom the part is bought from.
 */
export const applyListPriceVendorChange = <
  T extends {
    list_price_vendor_id: unknown;
    vendor_part_number: unknown;
    vendor_item_price: unknown;
    list_price: unknown;
  }
>(
  values: T,
  selectedVendor: LinkedVendorRow | null,
  originalListPrice: number | null
): T => {
  if (!selectedVendor) {
    return {
      ...values,
      list_price_vendor_id: null,
      vendor_part_number: "",
      vendor_item_price: "",
      list_price: originalListPrice,
    };
  }
  return {
    ...values,
    list_price_vendor_id: selectedVendor.vendor_id,
    vendor_part_number: selectedVendor.vendor_part_number ?? "",
    vendor_item_price: selectedVendor.vendor_item_price,
    list_price: selectedVendor.vendor_item_price,
  };
};
